import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Award,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Download,
  FileCheck,
  FileText,
  GraduationCap,
  Paperclip,
  Search,
  Sparkles,
  UserX,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FilePreviewModal } from '@/components/ui/FilePreviewModal';
import { QueryError } from '@/components/ui/QueryError';
import { gradeToneClasses } from '@/constants/grading';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadPrivateFile } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { getInitials } from '@/lib/utils';
import type { ActivityDetail, ActivitySubmission as ApiSubmission } from '@/types/activity';
import { ACTIVITY_TYPE_LABEL } from '../activities/constants';
import { describeScore, formatFileSize, gradeFor } from '../activities/utils';
import { activitiesApi } from '../api/activities.api';
import type { ActivitySubmission, ClassActivity } from '../types/teacher';

/**
 * Marking one class activity.
 *
 * The screen — the workbench, the roster rail, the quick score presets, the
 * one-click feedback tags and the document preview — is unchanged. Only where
 * its data comes from is: the activity and its submissions used to be read out
 * of `data/activityData.ts`, so every teacher saw the same five children and
 * any mark given vanished on reload.
 *
 * The activity and its whole roster arrive in one call, because the screen
 * cannot draw either without the other and two requests would let them
 * disagree. Every child in the section is listed — including the ones who have
 * handed nothing in, which is the row a teacher most needs to see.
 */

// Quick Preset Feedback Tags for 1-Click Grading
const QUICK_FEEDBACK_TAGS = [
  '⭐ Outstanding Work',
  '✅ Well Presented',
  '📐 Accurate Calculations',
  '🎨 Creative Approach',
  '📝 Needs Neater Presentation',
  '🔁 Revise & Resubmit',
];

const AVATAR_COLORS = [
  'bg-purple-500/15 text-purple-600',
  'bg-blue-500/15 text-blue-600',
  'bg-emerald-500/15 text-emerald-600',
  'bg-amber-500/15 text-amber-600',
  'bg-rose-500/15 text-rose-600',
  'bg-indigo-500/15 text-indigo-600',
];

/**
 * A stable avatar colour for one child.
 *
 * Keyed on the uuid rather than the roll number, which not every child has, or
 * the row's position, which moves whenever the list is filtered.
 */
function avatarStyleFor(studentId: string): string {
  let sum = 0;
  for (const char of studentId) sum += char.charCodeAt(0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

/**
 * Whether a roster row answers the search box: a name, the child's real roll
 * number, or something in their note.
 */
function matchesSearch(submission: ActivitySubmission, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return (
    submission.studentName.toLowerCase().includes(needle) ||
    submission.rollNo?.toLowerCase() === needle ||
    Boolean(submission.studentNotes?.toLowerCase().includes(needle))
  );
}

function getSafeInitials(name?: string): string {
  if (!name) return 'ST';
  const parts = name.trim().split(' ');
  const first = parts[0] || 'S';
  const last = parts.length > 1 ? parts[parts.length - 1] : first;
  return getInitials(first, last);
}

/** What the API returns, in the shape this screen draws. */
function toScreenActivity(detail: ActivityDetail): ClassActivity {
  const activity = detail.activity;

  return {
    id: activity.uuid,
    title: activity.title,
    // The console's own wording, not a second copy of it: this screen used to
    // keep its own map, in which an art & craft activity read "Individual
    // Project" — a label for a kind of work it is not (§3).
    activityType: ACTIVITY_TYPE_LABEL[activity.activityType],
    subject: activity.subject.name,
    className: activity.classSection.displayName,
    section: '',
    assignedDate: activity.assignedDate,
    dueDate: activity.dueDate,
    // Null means the activity is not scored. It stays null rather than becoming
    // zero, which drew "0 Points" and a score of x/0 for work nobody marks.
    maxPoints: activity.maxPoints,
    description: activity.description,
    instructions: activity.instructions ?? undefined,
    attachmentName: activity.guideline?.name,
    submissionCount: activity.submissionCount,
    totalStudents: activity.totalStudents,
    status: activity.status === 'OPEN' ? 'ACTIVE' : 'COMPLETED',
  };
}

/**
 * One child's row.
 *
 * `id` is the *student's* uuid on purpose: the grading call addresses a child
 * rather than a submission row, because a child who has handed nothing in has
 * no row yet and still has to be markable.
 */
function toScreenSubmission(
  student: ApiSubmission,
  activityUuid: string,
  maxPoints: number | null,
): ActivitySubmission {
  return {
    id: student.uuid,
    activityId: activityUuid,
    studentId: student.uuid,
    // The school's own roll number or nothing — a list position standing in for
    // one would be a number the school never gave this child.
    rollNo: student.rollNumber,
    studentName: student.fullName,
    // Kept as the timestamp it is and formatted where it is drawn — the raw
    // ISO string used to reach the screen and be printed as it stood.
    submittedAt: student.submittedAt ?? '',
    status: student.status,
    isLate: student.isLate,
    attachmentName: student.attachment?.name,
    attachmentSizeBytes: student.attachment?.sizeBytes ?? null,
    studentNotes: student.studentNotes ?? undefined,
    scoreObtained: student.scoreObtained ?? '',
    maxPoints,
    teacherFeedback: student.feedback ?? undefined,
  };
}

/**
 * When the work came in, in words.
 *
 * A submission with no timestamp is not a gap in the data: a model on the desk
 * or a chart on the wall is recorded by the teacher, and only an upload carries
 * a time of its own.
 */
function describeSubmittedAt(submission: ActivitySubmission): string {
  if (submission.status === 'PENDING') return 'Not handed in yet';
  if (!submission.submittedAt) return 'Recorded in class';

  return formatDateTime(submission.submittedAt);
}

export function TeacherActivityReviewPage() {
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();

  /** The activity and its whole roster, in one call. */
  const {
    data: detail,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => activitiesApi.get(activityId ?? ''), [activityId]);

  const activity: ClassActivity | null = useMemo(
    () => (detail ? toScreenActivity(detail) : null),
    [detail],
  );

  const submissionsList: ActivitySubmission[] = useMemo(
    () =>
      detail
        ? detail.students.map((student) =>
            toScreenSubmission(student, detail.activity.uuid, detail.activity.maxPoints),
          )
        : [],
    [detail],
  );

  // Search & Filtration States
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'SUBMITTED' | 'GRADED' | 'PENDING'>(
    'ALL',
  );

  // Compute Filtered Submissions safely
  const filteredSubmissions = submissionsList.filter(
    (sub) =>
      matchesSearch(sub, searchQuery) && (statusFilter === 'ALL' || sub.status === statusFilter),
  );

  // Selected Student for Detail Workbench View (Default to first in filteredSubmissions)
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Sync selectedStudentId whenever filteredSubmissions changes
  useEffect(() => {
    if (filteredSubmissions.length > 0) {
      const exists = filteredSubmissions.some((s) => s.id === selectedStudentId);
      if (!exists) {
        setSelectedStudentId(filteredSubmissions[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchQuery, submissionsList.length]);

  // Currently Selected Submission Record
  const activeSubmission =
    filteredSubmissions.find((s) => s.id === selectedStudentId) || filteredSubmissions[0];

  // Index of active submission within filtered list for Prev/Next navigation
  const activeIndex = activeSubmission
    ? filteredSubmissions.findIndex((s) => s.id === activeSubmission.id)
    : -1;

  // UI State: Rubric Instructions Expansion
  const [showInstructions, setShowInstructions] = useState(false);
  /** Whether the guidelines sheet is open in the preview. */
  const [isGuidelineOpen, setIsGuidelineOpen] = useState(false);

  // Metrics
  const gradedList = submissionsList.filter((s) => s.status === 'GRADED');
  const gradedCount = gradedList.length;
  const needsGradingCount = submissionsList.filter((s) => s.status === 'SUBMITTED').length;
  const pendingSubmissionCount = submissionsList.filter((s) => s.status === 'PENDING').length;
  const totalSubmissions = submissionsList.length;

  const totalScore = gradedList.reduce((acc, curr) => acc + (Number(curr.scoreObtained) || 0), 0);
  const averageScore = gradedCount > 0 ? (totalScore / gradedCount).toFixed(1) : 'N/A';

  function handleStatusTabChange(newStatus: 'ALL' | 'SUBMITTED' | 'GRADED' | 'PENDING') {
    setStatusFilter(newStatus);
    const newFiltered = submissionsList.filter(
      (sub) =>
        matchesSearch(sub, searchQuery) && (newStatus === 'ALL' || sub.status === newStatus),
    );

    if (newFiltered.length > 0) {
      setSelectedStudentId(newFiltered[0].id);
    }
  }

  async function handleSaveGrade(
    submissionId: string,
    score: number | '',
    feedback: string,
    autoAdvance = false,
  ) {
    if (!detail) return;
    setIsSaving(true);

    try {
      await activitiesApi.grade(detail.activity.uuid, submissionId, {
        // Empty is "no score", which clears one entered by mistake — not zero,
        // which would be a mark of zero.
        scoreObtained: score === '' ? null : Number(score),
        feedback,
      });

      const child = submissionsList.find((s) => s.id === submissionId);
      toast.success(`Saved for ${child?.studentName ?? 'this student'}`);

      // Auto-advance to next student in current filter list if requested
      if (autoAdvance && activeIndex >= 0 && activeIndex < filteredSubmissions.length - 1) {
        setSelectedStudentId(filteredSubmissions[activeIndex + 1].id);
      }

      refetch();
    } catch (caught) {
      // The server's messages name the reason — a score above the maximum, a
      // child off this roster — so they are worth showing verbatim.
      toast.error(
        'Could not save that mark',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Fetches one child's uploaded work with the access token and hands it over.
   *
   * The same route the homework review screen uses: the file sits behind the
   * read check on the activity and is never a public URL (CLAUDE.md §11).
   */
  async function handleDownloadSubmission(submission: ActivitySubmission) {
    if (!detail || !submission.attachmentName) return;

    try {
      await downloadPrivateFile(
        activitiesApi.submissionFilePath(detail.activity.uuid, submission.studentId),
        submission.attachmentName,
      );
    } catch (caught) {
      toast.error(
        'Could not download that file',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    }
  }

  function handleSelectPrevStudent() {
    if (activeIndex > 0) {
      setSelectedStudentId(filteredSubmissions[activeIndex - 1].id);
    }
  }

  function handleSelectNextStudent() {
    if (activeIndex >= 0 && activeIndex < filteredSubmissions.length - 1) {
      setSelectedStudentId(filteredSubmissions[activeIndex + 1].id);
    }
  }

  /**
   * Nothing below can be drawn until the activity arrives: the screen reads
   * from it in some thirty places, and a placeholder would be a page of zeroes
   * that looks like real marks.
   *
   * The three states are told apart rather than all drawn as "loading" — a
   * failed load used to spin here for ever, with no message and nothing to
   * press (§6).
   */
  if (!activity) {
    return (
      <div className="grid place-items-center py-16">
        {error ? (
          <div className="w-full max-w-md">
            <QueryError title="Could not load this activity" error={error} onRetry={refetch} />
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/teacher/activities')}
              className="mt-3 text-xs font-bold"
            >
              Back to activities
            </Button>
          </div>
        ) : isLoading ? (
          <div className="card p-6 flex flex-col items-center gap-3 text-center">
            <Sparkles className="h-7 w-7 text-accent animate-pulse" />
            <p className="text-sm font-bold text-ink">Loading this activity…</p>
            <p className="text-xs text-ink-2">
              Fetching the class list and what each child handed in.
            </p>
          </div>
        ) : (
          <div className="card p-6 flex flex-col items-center gap-3 text-center">
            <AlertCircle className="h-7 w-7 text-amber-500" />
            <p className="text-sm font-bold text-ink">This activity is no longer here</p>
            <p className="text-xs text-ink-2">
              It may have been withdrawn since this link was opened.
            </p>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/teacher/activities')}
              className="text-xs font-bold"
            >
              Back to activities
            </Button>
          </div>
        )}
      </div>
    );
  }

  /** Written guidelines, a guidelines sheet, or both — anything worth opening. */
  const hasGuidelines = Boolean(activity.instructions?.trim() || activity.attachmentName);

  return (
    <div className="flex flex-col gap-3">
      {/* Back Navigation Bar */}
      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/teacher/activities')}
          className="shadow-2xs cursor-pointer text-xs font-bold"
        >
          Back
        </Button>

        <span className="text-xs font-semibold text-ink-2 bg-surface px-3.5 py-1.5 rounded-pill border border-line shadow-2xs">
          Reviewing: <strong className="text-ink">{activity.className}</strong> ({activity.subject})
        </span>
      </div>

      {/* HERO ACTIVITY SPECIFICATION CARD */}
      <div className="card p-5 bg-surface border border-line rounded-[22px] shadow-sm flex flex-col gap-4 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        {/* Top Title & Metadata */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-line pb-3.5">
          <div className="flex items-start gap-3.5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-accent/15 text-accent border border-accent/25 shadow-2xs">
              <Sparkles className="h-6 w-6" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs sm:text-sm font-black uppercase tracking-wider px-2.5 py-0.5 rounded-pill bg-accent/10 text-accent border border-accent/20">
                  {activity.activityType}
                </span>
                <span className="text-xs sm:text-sm font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-pill bg-surface-2 text-ink-2 border border-line">
                  {activity.className} ({activity.subject})
                </span>
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-ink mt-1.5 leading-tight">
                {activity.title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="flex flex-col items-end">
              <span className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Max Marks
              </span>
              <span className="text-sm font-black text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3.5 py-1 rounded-pill border border-amber-500/25">
                {activity.maxPoints === null
                  ? describeScore(null, null)
                  : `${activity.maxPoints} Points`}
              </span>
            </div>
          </div>
        </div>

        {/* Guidelines Toggle */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm sm:text-base text-ink-2 font-medium leading-snug flex-1">
            {activity.description}
          </p>

          {/* Only when there is something to open — a toggle that reveals
              "none were added" is a click for nothing. */}
          {hasGuidelines && (
            <button
              type="button"
              onClick={() => setShowInstructions(!showInstructions)}
              aria-expanded={showInstructions}
              className="text-xs sm:text-sm font-bold text-accent hover:underline flex items-center gap-1 shrink-0 cursor-pointer"
            >
              {showInstructions ? 'Hide guidelines' : 'View guidelines'}
              {showInstructions ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        {hasGuidelines && showInstructions && (
          <div className="p-4 rounded-field bg-accent/5 border border-accent/20 text-sm text-ink-2 font-medium leading-relaxed animate-fade-in flex flex-col gap-2">
            <strong className="text-ink font-bold flex items-center gap-2 text-sm sm:text-base">
              <CheckCircle2 className="h-4.5 w-4.5 text-accent" /> Submission Guidelines:
            </strong>
            {activity.instructions?.trim() && (
              <p className="text-sm leading-relaxed">{activity.instructions}</p>
            )}
            {/* Opens in the same preview as the activities list, Download inside it. */}
            {activity.attachmentName && (
              <button
                type="button"
                onClick={() => setIsGuidelineOpen(true)}
                aria-label={`Preview ${activity.attachmentName}`}
                className="mt-1 flex w-fit max-w-full items-center gap-2 rounded-field text-left text-sm font-bold text-accent hover:underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
              >
                <Paperclip className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">Reference Attachment: {activity.attachmentName}</span>
              </button>
            )}
          </div>
        )}

        {/* Metrics Summary Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 pt-1">
          <div className="p-3 rounded-card bg-surface-2/60 border border-line flex items-center gap-3">
            <GraduationCap className="h-4.5 w-4.5 text-accent shrink-0" />
            <div>
              <p className="text-xs font-bold text-ink-3 uppercase">Total Students</p>
              <p className="text-sm font-black text-ink">{activity.totalStudents} Students</p>
            </div>
          </div>

          <div className="p-3 rounded-card bg-surface-2/60 border border-line flex items-center gap-3">
            <FileCheck className="h-4.5 w-4.5 text-blue-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-ink-3 uppercase">Submitted</p>
              <p className="text-sm font-black text-ink">
                {totalSubmissions - pendingSubmissionCount}
              </p>
            </div>
          </div>

          <div className="p-3 rounded-card bg-surface-2/60 border border-line flex items-center gap-3">
            <CheckCircle2 className="h-4.5 w-4.5 text-emerald-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-ink-3 uppercase">Graded</p>
              <p className="text-sm font-black text-ink">{gradedCount} Graded</p>
            </div>
          </div>

          <div className="p-3 rounded-card bg-surface-2/60 border border-line flex items-center gap-3">
            <Clock className="h-4.5 w-4.5 text-orange-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-ink-3 uppercase">Needs Review</p>
              <p className="text-sm font-black text-ink">{needsGradingCount} Pending</p>
            </div>
          </div>

          <div className="col-span-2 sm:col-span-1 p-3 rounded-card bg-surface-2/60 border border-line flex items-center gap-3">
            <Award className="h-4.5 w-4.5 text-purple-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-ink-3 uppercase">Avg Class Score</p>
              <p className="text-sm font-black text-ink">
                {activity.maxPoints === null
                  ? describeScore(null, null)
                  : `${averageScore} / ${activity.maxPoints}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SPLIT MASTER-DETAIL WORKBENCH GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        {/* LEFT MASTER ROSTER COLUMN (5 cols) */}
        <div className="lg:col-span-4 flex flex-col gap-3">
          {/* Filter Tabs & Search Header */}
          <div className="card p-3.5 bg-surface border border-line rounded-[20px] shadow-2xs flex flex-col gap-3 text-xs">
            {/* Search Box */}
            <div className="flex items-center gap-2 bg-surface-2 px-3 py-2 rounded-field border border-line">
              <Search className="h-4 w-4 text-ink-3 shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search student name or roll #..."
                className="w-full bg-transparent text-xs font-bold text-ink outline-none border-none shadow-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-ink-3 hover:text-ink cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Status Filter Pill Tabs Bar */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleStatusTabChange('ALL')}
                className={`px-3 py-1.5 rounded-pill text-xs font-extrabold transition-all cursor-pointer ${
                  statusFilter === 'ALL'
                    ? 'bg-accent text-white shadow-2xs border border-accent'
                    : 'bg-surface-2 text-ink-2 hover:text-ink border border-line'
                }`}
              >
                All ({totalSubmissions})
              </button>
              <button
                type="button"
                onClick={() => handleStatusTabChange('SUBMITTED')}
                className={`px-3 py-1.5 rounded-pill text-xs font-extrabold transition-all cursor-pointer flex items-center gap-1.5 ${
                  statusFilter === 'SUBMITTED'
                    ? 'bg-blue-600 text-white shadow-2xs border border-blue-600'
                    : 'bg-blue-500/10 text-blue-600 border border-blue-500/30'
                }`}
              >
                <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                Review ({needsGradingCount})
              </button>
              <button
                type="button"
                onClick={() => handleStatusTabChange('GRADED')}
                className={`px-3 py-1.5 rounded-pill text-xs font-extrabold transition-all cursor-pointer ${
                  statusFilter === 'GRADED'
                    ? 'bg-emerald-600 text-white shadow-2xs border border-emerald-600'
                    : 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30'
                }`}
              >
                Graded ({gradedCount})
              </button>
              <button
                type="button"
                onClick={() => handleStatusTabChange('PENDING')}
                className={`px-3 py-1.5 rounded-pill text-xs font-extrabold transition-all cursor-pointer flex items-center gap-1.5 ${
                  statusFilter === 'PENDING'
                    ? 'bg-orange-500 text-white shadow-2xs border border-orange-500'
                    : 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30'
                }`}
              >
                <UserX className="h-3.5 w-3.5" />
                Pending ({pendingSubmissionCount})
              </button>
            </div>
          </div>

          {/* Student Roster Cards List */}
          <div className="flex flex-col gap-2 max-h-[620px] overflow-y-auto pr-1">
            {filteredSubmissions.length === 0 ? (
              <div className="card p-8 text-center bg-surface border border-line rounded-[18px] text-xs text-ink-3">
                No student records match filter.
              </div>
            ) : (
              filteredSubmissions.map((sub) => {
                const isSelected = sub.id === activeSubmission?.id;
                const avatarStyle = avatarStyleFor(sub.studentId);
                const initials = getSafeInitials(sub.studentName);

                return (
                  <div
                    key={sub.id}
                    onClick={() => setSelectedStudentId(sub.id)}
                    className={`group p-3.5 rounded-[16px] border text-xs cursor-pointer transition-all flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'bg-accent/10 border-accent shadow-xs border-l-4 border-l-accent'
                        : 'bg-surface hover:bg-surface-2 border-line hover:border-line-strong'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Avatar */}
                      <span
                        className={`grid h-10 w-10 shrink-0 place-items-center rounded-[12px] font-black text-sm border ${avatarStyle}`}
                      >
                        {initials}
                      </span>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-ink truncate text-sm sm:text-base">
                            {sub.studentName}
                          </span>
                          {sub.rollNo && (
                            <span className="text-xs font-extrabold px-2 py-0.5 rounded-pill bg-surface-3 text-ink-2 border border-line shrink-0">
                              #{sub.rollNo}
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-ink-3 font-semibold truncate mt-0.5">
                          {sub.attachmentName ? sub.attachmentName : 'No file attached'}
                        </p>
                      </div>
                    </div>

                    {/* Status Indicator Pill */}
                    <div className="shrink-0">
                      {sub.status === 'GRADED' && (
                        <span className="px-3 py-1 rounded-pill bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 text-xs sm:text-sm font-black">
                          {describeScore(
                            sub.scoreObtained === '' || sub.scoreObtained === undefined
                              ? null
                              : sub.scoreObtained,
                            activity.maxPoints,
                          )}
                        </span>
                      )}
                      {sub.status === 'SUBMITTED' && (
                        <span className="px-3 py-1 rounded-pill bg-blue-500/15 text-blue-600 border border-blue-500/30 text-xs sm:text-sm font-black flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" /> Review
                        </span>
                      )}
                      {sub.status === 'PENDING' && (
                        <span className="px-3 py-1 rounded-pill bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/30 text-xs sm:text-sm font-extrabold">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT DETAIL EVALUATION WORKBENCH COLUMN (7 cols) */}
        <div className="lg:col-span-8">
          {activeSubmission ? (
            <EvaluationWorkbenchPanel
              key={activeSubmission.id}
              submission={activeSubmission}
              maxPoints={activity.maxPoints}
              activeIndex={activeIndex}
              totalCount={filteredSubmissions.length}
              onPrev={handleSelectPrevStudent}
              onNext={handleSelectNextStudent}
              isSaving={isSaving}
              canGrade={detail?.canGrade ?? false}
              onSaveGrade={(score, feedback, autoAdvance) =>
                handleSaveGrade(activeSubmission.id, score, feedback, autoAdvance)
              }
              onDownload={() => handleDownloadSubmission(activeSubmission)}
            />
          ) : (
            <div className="card p-12 text-center bg-surface border border-line rounded-[22px] text-xs text-ink-3">
              Select a student from the left roster to begin grading.
            </div>
          )}
        </div>
      </div>

      <FilePreviewModal
        source={
          isGuidelineOpen && activity.attachmentName
            ? { path: activitiesApi.guidelinePath(activity.id) }
            : null
        }
        fileName={activity.attachmentName ?? 'Guidelines'}
        onClose={() => setIsGuidelineOpen(false)}
      />
    </div>
  );
}

// FOCUSED EVALUATION WORKBENCH PANEL FOR SELECTED STUDENT
function EvaluationWorkbenchPanel({
  submission,
  maxPoints,
  activeIndex,
  totalCount,
  onPrev,
  onNext,
  onSaveGrade,
  onDownload,
  isSaving,
  canGrade,
}: {
  submission: ActivitySubmission;
  /** Null for an unscored activity: feedback only, no marks to enter. */
  maxPoints: number | null;
  activeIndex: number;
  totalCount: number;
  onPrev: () => void;
  onNext: () => void;
  onSaveGrade: (score: number | '', feedback: string, autoAdvance?: boolean) => void;
  /** Fetches this child's uploaded file behind the caller's own token. */
  onDownload: () => void;
  /** True while a mark is in flight, so the buttons cannot be pressed twice. */
  isSaving?: boolean;
  /**
   * Whether this caller may record marks, as the server decided it. A colleague
   * who may read the class but not mark it gets the work and the feedback
   * already given, without a Save button that would only come back refused.
   */
  canGrade: boolean;
}) {
  const [score, setScore] = useState<number | ''>(submission.scoreObtained ?? '');
  const [feedback, setFeedback] = useState<string>(submission.teacherFeedback || '');
  const [isSaved, setIsSaved] = useState<boolean>(submission.status === 'GRADED');

  const avatarStyle = avatarStyleFor(submission.studentId);
  const initials = getSafeInitials(submission.studentName);
  /** Empty where the size was never recorded — see `formatFileSize`. */
  const attachmentSize = formatFileSize(submission.attachmentSizeBytes ?? null);

  function handleSave(autoAdvance = false) {
    onSaveGrade(score, feedback, autoAdvance);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2500);
  }

  function handleSelectPresetScore(outOf: number, ratio: number) {
    setScore(Math.round(outOf * ratio));
  }

  function handleAppendTag(tag: string) {
    if (feedback.includes(tag)) return;
    setFeedback((prev) => (prev ? `${prev} ${tag}` : tag));
  }

  // Both null for an unscored activity — and for one marked out of zero, where a
  // bare division is Infinity — so neither can reach the badge.
  const scorePercent =
    score !== '' && maxPoints !== null && maxPoints > 0
      ? Math.round((score / maxPoints) * 100)
      : null;
  const grade = gradeFor(score === '' ? null : score, maxPoints);

  return (
    <div className="card p-5 bg-surface border border-line rounded-[22px] shadow-sm flex flex-col gap-4 text-xs animate-fade-in sticky top-20">
      {/* Workbench Navigation Header */}
      <div className="flex items-center justify-between border-b border-line pb-3.5">
        <div className="flex items-center gap-3">
          <span
            className={`grid h-12 w-12 place-items-center rounded-[14px] font-black text-base border shadow-2xs ${avatarStyle}`}
          >
            {initials}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg sm:text-xl font-black text-ink">{submission.studentName}</h2>
              {submission.rollNo && (
                <span className="text-xs sm:text-sm font-extrabold px-2.5 py-0.5 rounded-pill bg-surface-3 text-ink border border-line">
                  Roll #{submission.rollNo}
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-ink-3 font-semibold mt-0.5 flex flex-wrap items-center gap-1.5">
              <Clock className="h-4 w-4 text-accent" />
              Submitted: <strong className="text-ink">{describeSubmittedAt(submission)}</strong>
              {submission.isLate && (
                <span className="px-2 py-0.5 rounded-pill bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 text-xs font-extrabold">
                  Late
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Prev / Next Student Quick Nav synchronized strictly with active filter roster */}
        <div className="flex items-center gap-2 bg-surface-2 p-1.5 rounded-pill border border-line">
          <button
            type="button"
            disabled={activeIndex <= 0}
            onClick={onPrev}
            className="p-1.5 rounded-pill bg-surface hover:bg-surface-3 text-ink-2 hover:text-ink disabled:opacity-30 disabled:pointer-events-none border border-line transition-all cursor-pointer shadow-2xs"
            title="Previous Student in active list"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <span className="text-xs sm:text-sm font-bold text-ink-2 px-1">
            <strong className="text-ink">{activeIndex + 1}</strong> of {totalCount}
          </span>

          <button
            type="button"
            disabled={activeIndex >= totalCount - 1}
            onClick={onNext}
            className="p-1.5 rounded-pill bg-surface hover:bg-surface-3 text-ink-2 hover:text-ink disabled:opacity-30 disabled:pointer-events-none border border-line transition-all cursor-pointer shadow-2xs"
            title="Next Student in active list"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Nothing handed in: nothing to open, but the form below stays — the
          server takes a mark for a child with no submission row, which is how
          work handed over on paper or shown in class gets recorded. */}
      {submission.status === 'PENDING' ? (
        <div className="p-3.5 rounded-field bg-surface-2/40 border border-line/60 text-xs sm:text-sm text-ink-3 font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500 shrink-0" />
          <span>
            {submission.studentName} has not handed this in online. You can still record a mark
            or remark for work given to you in class.
          </span>
        </div>
      ) : (
        <>
        {/* Submitted File Box */}
        {submission.attachmentName ? (
          <div className="flex items-center justify-between p-3.5 rounded-[16px] bg-accent/5 border border-accent/20 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-accent/15 text-accent border border-accent/25">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink truncate">{submission.attachmentName}</p>
                {/* The size we recorded when it was stored, or nothing — never a
                    figure made up to fill the line. */}
                {attachmentSize && (
                  <p className="text-xs sm:text-sm text-ink-3 font-semibold">{attachmentSize}</p>
                )}
              </div>
            </div>

            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-4 w-4 text-accent" />}
              onClick={onDownload}
              className="text-xs sm:text-sm font-bold text-accent shrink-0"
            >
              Download
            </Button>
          </div>
        ) : (
          <div className="p-3.5 rounded-field bg-surface-2/40 border border-line/60 text-xs sm:text-sm text-ink-3 font-semibold italic flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
            <span>No digital file attachment uploaded by student for this activity.</span>
          </div>
        )}

        {/* Student Remarks */}
        {submission.studentNotes && (
          <div className="p-3.5 rounded-field bg-surface-2/70 border border-line text-sm text-ink-2 italic font-medium leading-relaxed">
            <strong className="text-ink not-italic font-bold">Student Note: </strong>"
            {submission.studentNotes}"
          </div>
        )}
        </>
      )}

        {/* GRADING & FEEDBACK FORM — only for a caller the server lets mark */}
        {canGrade ? (
          <div className="flex flex-col gap-3.5 pt-2 border-t border-line">
            <div className="flex items-center justify-between">
              <span className="text-sm font-extrabold text-ink flex items-center gap-1.5">
                <Award className="h-4.5 w-4.5 text-accent" />{' '}
                {maxPoints === null ? 'Enter Remarks' : 'Enter Grade & Remarks'}
              </span>

              {/* The letter from the shared grade bands, so this badge and the
                  exam screens never disagree; the percentage rides alongside. */}
              {grade && scorePercent !== null && (
                <span className={`text-xs sm:text-sm font-extrabold px-2.5 py-0.5 rounded-pill border ${gradeToneClasses(grade).badge}`}>
                  Grade {grade.grade} · {scorePercent}% ({score}/{maxPoints})
                </span>
              )}
              {maxPoints === null && (
                <span className="text-xs sm:text-sm font-extrabold text-ink-2 bg-surface-2 px-2.5 py-0.5 rounded-pill border border-line">
                  {describeScore(null, null)}
                </span>
              )}
            </div>

            {/* Unscored work takes remarks only: no presets, no marks box. */}
            {maxPoints !== null && (
            <>
            {/* Preset Score Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs sm:text-sm font-bold text-ink-3 mr-1">Preset Score:</span>
              <button
                type="button"
                onClick={() => handleSelectPresetScore(maxPoints, 1.0)}
                className="px-2.5 py-1 rounded-pill bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-600 border border-emerald-500/30 text-xs sm:text-sm font-extrabold transition-all cursor-pointer"
              >
                100% ({maxPoints}/{maxPoints})
              </button>
              <button
                type="button"
                onClick={() => handleSelectPresetScore(maxPoints, 0.9)}
                className="px-2.5 py-1 rounded-pill bg-blue-500/15 hover:bg-blue-500/25 text-blue-600 border border-blue-500/30 text-xs sm:text-sm font-extrabold transition-all cursor-pointer"
              >
                90% ({Math.round(maxPoints * 0.9)}/{maxPoints})
              </button>
              <button
                type="button"
                onClick={() => handleSelectPresetScore(maxPoints, 0.75)}
                className="px-2.5 py-1 rounded-pill bg-amber-500/15 hover:bg-amber-500/25 text-amber-600 border border-amber-500/30 text-xs sm:text-sm font-extrabold transition-all cursor-pointer"
              >
                75% ({Math.round(maxPoints * 0.75)}/{maxPoints})
              </button>
              <button
                type="button"
                onClick={() => handleSelectPresetScore(maxPoints, 0.5)}
                className="px-2.5 py-1 rounded-pill bg-rose-500/15 hover:bg-rose-500/25 text-rose-600 border border-rose-500/30 text-xs sm:text-sm font-extrabold transition-all cursor-pointer"
              >
                50% ({Math.round(maxPoints * 0.5)}/{maxPoints})
              </button>
            </div>

            {/* Score Input */}
            <div className="flex flex-col gap-1">
              <label className="text-xs sm:text-sm font-extrabold text-ink-3 uppercase tracking-wider">
                Marks Obtained (Max Points: {maxPoints})
              </label>
              <input
                type="number"
                max={maxPoints}
                min={0}
                value={score}
                onChange={(e) => setScore(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder={`Enter marks 0 - ${maxPoints}`}
                className="w-full rounded-field border border-line bg-surface p-2.5 text-sm font-black text-ink outline-none focus:border-accent shadow-2xs"
              />
            </div>
            </>
            )}

            {/* Remarks Input */}
            <div className="flex flex-col gap-1">
              <label className="text-xs sm:text-sm font-extrabold text-ink-3 uppercase tracking-wider">
                Teacher Feedback & Remarks
              </label>
              <textarea
                rows={2}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Enter remarks e.g. Outstanding working model and clear formula derivations..."
                className="w-full rounded-field border border-line bg-surface p-2.5 text-sm font-medium text-ink outline-none focus:border-accent resize-none shadow-2xs"
              />
            </div>

            {/* Quick Remarks Chips */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs sm:text-sm font-bold text-ink-3">Quick Tags:</span>
              {QUICK_FEEDBACK_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleAppendTag(tag)}
                  className="px-2.5 py-1 rounded-pill bg-surface-2 hover:bg-accent/15 hover:text-accent border border-line text-xs sm:text-sm font-semibold text-ink-2 transition-all cursor-pointer"
                >
                  + {tag}
                </button>
              ))}
            </div>

            {/* Save & Save + Next Buttons */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="text-xs sm:text-sm font-bold"
              >
                {isSaved ? 'Grade Saved! ✓' : 'Save Grade'}
              </Button>

              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSave(true)}
                disabled={isSaving}
                className={`text-xs sm:text-sm font-extrabold shadow-2xs transition-all ${
                  isSaved ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''
                }`}
              >
                Save & Grade Next Student →
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2 pt-2 border-t border-line">
            <span className="text-sm font-extrabold text-ink flex items-center gap-1.5">
              <Award className="h-4.5 w-4.5 text-accent" /> Marks and remarks
            </span>
            <p className="text-xs sm:text-sm text-ink-3 font-semibold">
              You can see this work, but marking it belongs to the teacher it is allotted to.
            </p>
            <p className="text-sm font-bold text-ink">
              {maxPoints === null
                ? describeScore(null, null)
                : submission.scoreObtained === '' || submission.scoreObtained === undefined
                  ? 'Not marked yet'
                  : describeScore(submission.scoreObtained, maxPoints)}
            </p>
            {submission.teacherFeedback && (
              <p className="p-3.5 rounded-field bg-surface-2/70 border border-line text-sm text-ink-2 font-medium leading-relaxed">
                {submission.teacherFeedback}
              </p>
            )}
          </div>
        )}
    </div>
  );
}
